import classNames from 'classnames';
import { Button } from 'components/atoms';
import InputMessage from 'components/atoms/inputs/wrapper/message/InputMessage';
import { FormikErrors, useFormikContext } from 'formik';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft, PiCaretRight, PiLock, PiWarning } from 'react-icons/pi';
import { TbArrowRight } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import './TabsNavigation.scss';

interface ITabsNavigation {
  name: string;
  value: number;
  error?: string | (string | undefined)[] | FormikErrors<any>[] | FormikErrors<any>;
  helper?: string;
  warning?: boolean;
  hideInEditMode?: boolean;
  hideInDisplay?: boolean;
}

interface ITabsNavigationProps  {
  tabs: ITabsNavigation[];
  disabled?: number[];
  displayMode?: boolean;
}

const TabsNavigation = ({  disabled, tabs, displayMode }: ITabsNavigationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { tokenData } = useStoreState();

  const { submitCount } = useFormikContext();

  const isTabActive = (name: string): boolean => pathname.split('/').includes(name);

  const isTabDisabled = (value: number): boolean => !!disabled?.includes(value);

  const elementRef = useRef<HTMLDivElement | null>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    if (elementRef.current) {
      const isHorOverflowing = elementRef.current.scrollWidth > elementRef.current.clientWidth;
      setIsOverflowing(isHorOverflowing);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    if (elementRef.current) {
      const scrollAmount = elementRef.current.clientWidth * 0.8;
      if (direction === 'right') {
        elementRef.current.scrollLeft += scrollAmount;
      } else {
        elementRef.current.scrollLeft -= scrollAmount;
      }
    }
  };

  const managedTabs = useMemo(
    () =>
      (displayMode ? tabs : tabs.filter((t) => !t.hideInEditMode)).sort(
        (a, b) => a.value - b.value,
      ),
    [tabs, displayMode],
  );

  const className = classNames(['tab-container'], { displayMode });

  return (
    <div className={className}>
      {isOverflowing && (
        <button
          type="button"
          className="tab-arrow tab-arrow--left"
          onClick={() => handleScroll('left')}
        >
          <PiCaretLeft />
        </button>
      )}

      <div
        ref={elementRef}
        className="tabs-navigation"
        style={{
          overflowX: isOverflowing ? 'auto' : undefined,
          justifyContent: !isOverflowing ? 'center' : 'flex-start',
          overflowY: !isOverflowing ? undefined : 'hidden',
        }}
      >
        {managedTabs.map((tab, index) => {
          const hasError = tab.error || !!tab.helper?.length;

          const buttonClassNames = [
            'button',
            !!tab.error ? 'error' : !!tab.warning ? 'warning' : undefined,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <>
              <Button
                key={tab.name}
                wrapperClassNames={hasError && submitCount > 0 ? 'is-message' : undefined}
                className={buttonClassNames}
                disabled={!tab.error && isTabDisabled(tab.value)}
                variant={
                  hasError && submitCount > 0 ? BUTTON_VARIANT.remove : BUTTON_VARIANT.default
                }
                isClear
                size="compact"
                isActive={isTabActive(tab.name)}
                onClick={() =>
                  navigate(tab.name, {
                    relative: 'path',
                    replace: true,
                  })
                }
              >
                {!tab.error && isTabDisabled(tab.value) && <Button.Icon icon={<PiLock />} />}
                {hasError && submitCount > 0 && <Button.Icon icon={<PiWarning />} />}
                <Button.Text text={t(`tabs.${tab.name}`)} />
                {hasError && submitCount > 0 && (
                  <InputMessage
                    helperText={tab.helper}
                    message={typeof tab.error === 'string' ? tab.error : t('error-code.FORM_ERROR')}
                  />
                )}
              </Button>

              {index !== managedTabs.length - 1 && (
                <Button className="arrow-step" onlyIcon size="compact" isClear>
                  <Button.Icon icon={<TbArrowRight />} />
                </Button>
              )}
            </>
          );
        })}
      </div>

      {isOverflowing && (
        <button
          type="button"
          className="tab-arrow tab-arrow--right"
          onClick={() => handleScroll('right')}
        >
          <PiCaretRight />
        </button>
      )}
    </div>
  );
};

export default TabsNavigation;
